import type { ExecutionContext } from './context/execution-context'
import type { Extension } from './extension'
import { ExtensionMethod } from './method'
import { argsFromParams } from './model/method-model'
import type { SharedExtensionMethod } from './model/model-types'
import { toOpaqueBodyParams } from './model/params-model'
import type { ZodSchema } from './params'
import { MethodParams } from './params'
import type { AnySchema } from './types'

// The runtime-owned methods — the frozen v1 seam the platform calls rather than a flow: the OAuth connect /
// callback / refresh steps, the polling tick, and the five realtime webhook lifecycle hooks. An author
// never writes one; they exist because a feature was set up.
//
// Mechanically they are all the same: a runtime-owned params scheme, a SYSTEM model entry, and a handler.
// Only the handler differs — so there is ONE class, driven by a spec, and each feature declares its own
// specs in one place (see each owner's `#registerSystemMethods`). That single row now holds every fact
// about a SYSTEM method: its name, its input contract, its model shape, and what it does.

export interface SystemMethodSpec {
  name: string
  /**
   * The params this method takes, as a normal z.object. Today every SYSTEM method happens to declare
   * exactly ONE property (`invocation` / `callbackObject` / `refreshToken`) — but that is a fact about the
   * current seam, not a constraint of the machinery: a second property needs no change here. Omit entirely
   * for a method that takes no input (getOAuth2ConnectionURL).
   */
  scheme?: AnySchema
  // The v1 `returnType` token. OBJECT for everything but the connect step, which answers a URL.
  returnType?: string
  // Set false for a seam that must stay callable without being advertised (see ExtensionMethod.published).
  published?: boolean
  // Receives the validated params, exactly as an action's handler receives its own.
  run: (params: Record<string, unknown>, ctx: ExecutionContext) => unknown
}

export class SystemMethod extends ExtensionMethod {
  readonly params: MethodParams

  constructor(extension: Extension, private readonly spec: SystemMethodSpec) {
    super(extension)

    this.params = MethodParams.of(spec.scheme, { serviceId: extension.id, kind: 'system method', itemId: spec.name, slot: 'scheme' })
  }

  get name(): string {
    return this.spec.name
  }

  get registerAs(): string {
    return 'SYSTEM'
  }

  get published(): boolean {
    return this.spec.published ?? true
  }

  /**
   * FIELD-BY-FIELD validation: every declared param is required, then checked against its OWN schema. The
   * input here is a wrapper carrying named params rather than the params themselves — `{ invocation }` /
   * `{ callbackObject }` / `{ refreshToken }` are separate method params.
   *
   * Deliberately not one `parse(methodData)` over the whole object, because two failures must stay
   * distinguishable: an ABSENT param is a malformed call (MissingMethodParamError naming the param), while a
   * bad VALUE is a malformed value (InvalidMethodInputError whose issue paths are relative to that param,
   * not to `methodData`). A whole-object parse would flatten both into the second, with `invocation.` in
   * front of every path.
   */
  validate(methodData: Record<string, unknown>): Record<string, unknown> {
    const input = this.input(methodData)
    const parsed: Record<string, unknown> = {}

    for (const [name, schema] of this.params.fields()) {
      parsed[name] = input.parseField(name, schema as ZodSchema)
    }

    return parsed
  }

  execute(params: Record<string, unknown>, ctx: ExecutionContext): unknown {
    return this.spec.run(params, ctx)
  }

  toModel(): SharedExtensionMethod {
    const bodyParams = toOpaqueBodyParams(this.params)

    return {
      name: this.name,
      operationName: this.name,
      description: '',
      registerAs: this.registerAs,
      category: '',
      colorStart: '',
      colorEnd: '',
      customIcon: undefined,
      returnType: this.spec.returnType ?? 'OBJECT',
      queryParams: [],
      bodyParams,
      metaInfo: { args: argsFromParams(bodyParams), sampleResult: null, sampleResultLoader: null },
    }
  }
}
